import { create } from 'zustand'
import type { User } from 'firebase/auth'
import type { VibeUser } from '@vibe/shared-types'

/**
 * 인증 상태 — Firebase user + 백엔드(`/api/auth/me`)에서 가져온 사용자 프로필.
 *
 * 흐름:
 *   1) firebase.ts subscribeIdToken → Firebase user 변화 감지
 *   2) user 가 생기면 백엔드 /api/auth/me 호출 → VibeUser 프로필 받기
 *   3) profile 까지 채워지면 status='ready'
 */

export type AuthStatus = 'loading' | 'signedOut' | 'signingIn' | 'ready' | 'error'

interface AuthState {
  status: AuthStatus
  firebaseUser: User | null
  profile: VibeUser | null
  idToken: string | null
  error: string | null
  setFirebaseUser: (user: User | null) => void
  setProfile: (profile: VibeUser | null) => void
  setIdToken: (token: string | null) => void
  setStatus: (status: AuthStatus) => void
  setError: (error: string | null) => void
  reset: () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'loading',
  firebaseUser: null,
  profile: null,
  idToken: null,
  error: null,
  setFirebaseUser: (user) => set({ firebaseUser: user }),
  setProfile: (profile) => set({ profile }),
  setIdToken: (token) => set({ idToken: token }),
  setStatus: (status) => set({ status }),
  setError: (error) => set({ error, status: error ? 'error' : 'ready' }),
  reset: () =>
    set({
      status: 'signedOut',
      firebaseUser: null,
      profile: null,
      idToken: null,
      error: null,
    }),
}))

/**
 * 비-React 컨텍스트 (api.ts 같은 모듈)에서 즉시 토큰 읽기.
 * Zustand store는 React Hook 밖에서도 `.getState()` 로 접근 가능.
 */
export function getCurrentIdToken(): string | null {
  return useAuthStore.getState().idToken
}
